import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FaChevronDown } from 'react-icons/fa'
import supabase from '../services/supabase'

const CorrespondingCodes = ({ correspondingCodes, sourceType }) => {
  const [expandedCode, setExpandedCode] = useState(null)
  const [codeDetails, setCodeDetails] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  if (!correspondingCodes || correspondingCodes.length === 0) {
    return null
  }

  // Determine the target type based on the source type
  const targetType = sourceType === 'cnae' ? 'cnae2009' : 'cnae'

  const toggleAccordion = async (code, index) => {
    // If clicking on the already expanded code, close it
    if (expandedCode === index) {
      setExpandedCode(null)
      return
    }

    setExpandedCode(index)

    // If we already have the details for this code, no need to fetch again
    if (codeDetails[code.code]) {
      return
    }

    setIsLoading(true)

    try {
      let details = null

      if (targetType === 'cnae2009') {
        // Fetch CNAE 2009 details
        const { data, error } = await supabase.rpc(
          'get_cnae_2009_overview_by_code',
          {
            cnae_code: code.code,
          }
        )

        if (error) throw error
        if (data && data.length > 0) {
          details = {
            code: data[0].cod_cnae_2009,
            title: data[0].titulo_cnae_2009,
            description: data[0].descripcion_cnae_2009,
            category: data[0].categoria_cnae_2009,
            level: data[0].nivel_cnae_2009,
            slug: data[0].slug_cnae_2009,
          }
        }
      } else if (targetType === 'cnae') {
        // Fetch CNAE 2025 details
        const { data, error } = await supabase.rpc(
          'get_cnae_2025_overview_by_code',
          {
            cnae_code: code.code,
          }
        )

        if (error) throw error
        if (data && data.length > 0) {
          details = {
            code: data[0].cod_cnae,
            title: data[0].titulo_cnae,
            description: data[0].descripcion_cnae,
            category: data[0].categoria_cnae,
            level: data[0].nivel_cnae,
            slug: data[0].slug_cnae,
          }
        }
      }

      if (details) {
        setCodeDetails((prev) => ({
          ...prev,
          [code.code]: details,
        }))
      }
    } catch (error) {
      console.error(`Error fetching ${targetType} details:`, error)
    } finally {
      setIsLoading(false)
    }
  }

  // Format description text with proper line breaks
  const formatDescription = (description) => {
    if (!description) return null

    // Split by line breaks and filter out empty lines
    const paragraphs = description
      .split('\n')
      .filter((line) => line.trim() !== '')

    return paragraphs.map((paragraph, index) => (
      <p key={index} className='mb-3'>
        {paragraph}
      </p>
    ))
  }

  return (
    <section className='bg-surface rounded-lg p-6 my-6'>
      <h2 className='text-l font-bold mb-4 text-start text-text-primary'>
        Códigos Correspondientes
      </h2>

      <div className='space-y-2'>
        {correspondingCodes.map((code, index) => (
          <div key={index} className='rounded-md overflow-hidden'>
            {/* Accordion Header */}
            <div
              className={`flex items-center justify-between p-3 cursor-pointer bg-card ${
                targetType === 'cnae2009' ? 'text-cnae2009' : 'text-cnae'
              }`}
              onClick={() => toggleAccordion(code, index)}
            >
              <div className='flex flex-col'>
                <div className='flex items-center space-x-2 mb-1'>
                  <span
                    className={`px-2 py-1 text-xs font-bold rounded ${
                      targetType === 'cnae2009'
                        ? 'bg-cnae2009 text-background'
                        : 'bg-cnae text-background'
                    }`}
                  >
                    {targetType === 'cnae2009' ? 'CNAE 2009' : 'CNAE 2025'}
                  </span>
                </div>
                <div className='flex flex-col items-start'>
                  <span className='font-bold text-text-primary'>
                    {code.code}
                  </span>
                  <span className='text-text-primary text-start'>
                    {code.title}
                  </span>
                </div>
              </div>
              <FaChevronDown
                className={`text-text-secondary transition-transform duration-300 ${
                  expandedCode === index ? 'rotate-180' : ''
                }`}
              />
            </div>

            {/* Accordion Content */}
            {expandedCode === index && (
              <div className='p-4 bg-card border-t border-border'>
                {isLoading ? (
                  <div className='py-4 text-text-secondary'>
                    Cargando detalles...
                  </div>
                ) : codeDetails[code.code] ? (
                  <div className='space-y-4 text-left'>
                    <div>
                      <h3
                        className={`text-sm font-medium mb-1 ${
                          targetType === 'cnae2009'
                            ? 'text-cnae2009'
                            : 'text-cnae'
                        }`}
                      >
                        Descripción
                      </h3>
                      <div className='text-text-primary'>
                        {formatDescription(codeDetails[code.code].description)}
                      </div>
                    </div>

                    <div>
                      <h3
                        className={`text-sm font-medium mb-1 ${
                          targetType === 'cnae2009'
                            ? 'text-cnae2009'
                            : 'text-cnae'
                        }`}
                      >
                        Categoría
                      </h3>
                      <p className='text-text-primary'>
                        {codeDetails[code.code].category}
                      </p>
                    </div>

                    <div>
                      <h3
                        className={`text-sm font-medium mb-1 ${
                          targetType === 'cnae2009'
                            ? 'text-cnae2009'
                            : 'text-cnae'
                        }`}
                      >
                        Nivel
                      </h3>
                      <p className='text-text-primary'>
                        {codeDetails[code.code].level}
                      </p>
                    </div>

                    <div className='pt-2'>
                      <Link
                        to={
                          targetType === 'cnae'
                            ? `/cnae-2025/${codeDetails[code.code].code.replace(
                                '.',
                                ''
                              )}/${codeDetails[code.code].slug}`
                            : targetType === 'cnae2009'
                            ? `/cnae-2009/${codeDetails[code.code].code.replace(
                                '.',
                                ''
                              )}/${codeDetails[code.code].slug}`
                            : `/${targetType}/${codeDetails[code.code].slug}`
                        }
                        className='flex items-center justify-center w-full p-2 bg-transparent border border-text-secondary rounded-md text-text-primary hover:bg-opacity-10 hover:bg-white transition-colors'
                      >
                        Ver Más Detalles <span className='ml-1'>→</span>
                      </Link>
                    </div>
                  </div>
                ) : (
                  <p className='text-text-secondary py-2'>
                    Cargando detalles...
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  )
}

export default CorrespondingCodes
